import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useToastStore } from '../../../stores/toastStore';
import { getPromptDefault, PromptKinds, type PromptKind, type PromptPreviewResultResponse } from '../api';
import { PromptVersionComparison, type PromptVersionInfo } from '../components/PromptVersionComparison';
import {
  useActivatePromptVersion,
  useCreatePromptVersion,
  usePreviewPrompt,
  usePromptTemplate,
  usePromptTemplateVersion,
} from '../hooks/useAdminPrompts';

const KIND_TITLES: Record<PromptKind, string> = {
  Extraction: 'Extracción de campos',
  Discovery: 'Descubrimiento de schema',
  ProviderTest: 'Test de conectividad',
  Segmentation: 'Segmentación',
  Reeducation: 'Reeducación',
  Classification: 'Clasificación de tipo',
};

function isPromptKind(value: string | undefined): value is PromptKind {
  return (
    value === 'Extraction' ||
    value === 'Discovery' ||
    value === 'ProviderTest' ||
    value === 'Segmentation' ||
    value === 'Reeducation' ||
    value === 'Classification'
  );
}

/** Editor completo de una plantilla: system + user templates, lista de versiones, preview y rollback. */
export function PromptEditorPage(): JSX.Element {
  const params = useParams<{ kind?: string }>();
  const rawKind = params.kind;
  const kind: PromptKind = isPromptKind(rawKind) ? rawKind : PromptKinds.Extraction;

  const detail = usePromptTemplate(kind);
  const createMutation = useCreatePromptVersion(kind);
  const activateMutation = useActivatePromptVersion(kind);
  const previewMutation = usePreviewPrompt(kind);
  const pushToast = useToastStore((s) => s.push);

  const [systemDraft, setSystemDraft] = useState<string>('');
  const [userDraft, setUserDraft] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewResult, setPreviewResult] = useState<PromptPreviewResultResponse | null>(null);

  const selectedVersion = usePromptTemplateVersion(kind, selectedVersionId);

  // Cuando carga el detalle, prellena el editor con la versión activa.
  useEffect(() => {
    if (detail.data !== undefined && selectedVersionId === null) {
      setSystemDraft(detail.data.activeSystemTemplate);
      setUserDraft(detail.data.activeUserTemplate ?? '');
    }
  }, [detail.data, selectedVersionId]);

  // Cuando el admin selecciona una versión, carga ese cuerpo en el editor.
  useEffect(() => {
    if (selectedVersion.data !== undefined) {
      setSystemDraft(selectedVersion.data.systemTemplate);
      setUserDraft(selectedVersion.data.userTemplate ?? '');
    }
  }, [selectedVersion.data]);

  const expectsUserTemplate = kind !== PromptKinds.Discovery;
  const tokens = detail.data?.tokens ?? [];
  const versions = useMemo(
    () => [...(detail.data?.versions ?? [])].sort((a, b) => b.number - a.number),
    [detail.data?.versions],
  );

  async function handleSave(): Promise<void> {
    try {
      const created = await createMutation.mutateAsync({
        systemTemplate: systemDraft,
        userTemplate: expectsUserTemplate ? userDraft : null,
        notes: notes.trim() === '' ? null : notes.trim(),
      });
      setSelectedVersionId(null);
      setNotes('');
      pushToast({ tone: 'success', title: `Versión v${created.number} guardada y activada.` });
    } catch (err) {
      pushToast({ tone: 'error', title: err instanceof Error ? err.message : 'No se pudo guardar.' });
    }
  }

  async function handleActivate(versionId: string): Promise<void> {
    try {
      await activateMutation.mutateAsync(versionId);
      setSelectedVersionId(null);
      pushToast({ tone: 'success', title: 'Versión activada.' });
    } catch (err) {
      pushToast({ tone: 'error', title: err instanceof Error ? err.message : 'No se pudo activar.' });
    }
  }

  async function handleLoadDefault(): Promise<void> {
    try {
      const defaults = await getPromptDefault(kind);
      setSystemDraft(defaults.systemTemplate);
      setUserDraft(defaults.userTemplate ?? '');
      setSelectedVersionId(null);
      pushToast({
        tone: 'info',
        title: 'Plantilla por defecto cargada en el editor. Guarda para crear una nueva versión.',
      });
    } catch (err) {
      pushToast({ tone: 'error', title: err instanceof Error ? err.message : 'No se pudo cargar la plantilla por defecto.' });
    }
  }

  async function handlePreview(): Promise<void> {
    try {
      const result = await previewMutation.mutateAsync({
        systemTemplate: systemDraft,
        userTemplate: expectsUserTemplate ? userDraft : null,
      });
      setPreviewResult(result);
      setPreviewOpen(true);
    } catch (err) {
      pushToast({ tone: 'error', title: err instanceof Error ? err.message : 'Preview falló.' });
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-mono text-slate-500">/admin/prompts/{kind}</p>
          <h1 className="text-2xl font-semibold text-slate-900">{KIND_TITLES[kind]}</h1>
          <p className="mt-1 text-sm text-slate-500">
            La versión activa se guarda en Cosmos y se aplica de inmediato a todos los tenants.
          </p>
        </div>
        <Link to="/admin/prompts" className="text-sm text-slate-600 hover:text-primary">
          ← Volver a la lista
        </Link>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <section className="space-y-4">
          {detail.isError ? (
            <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              No se pudo cargar el detalle del prompt.
            </div>
          ) : null}

          <div className="card p-4">
            <label className="text-sm font-medium text-slate-700">System template</label>
            <textarea
              className="mt-2 w-full rounded-md border border-slate-300 bg-slate-50 p-3 font-mono text-xs leading-snug focus:border-violet-500 focus:bg-white focus:outline-none"
              rows={20}
              value={systemDraft}
              onChange={(e) => setSystemDraft(e.target.value)}
              spellCheck={false}
            />
          </div>

          {expectsUserTemplate ? (
            <div className="card p-4">
              <label className="text-sm font-medium text-slate-700">User template</label>
              <textarea
                className="mt-2 w-full rounded-md border border-slate-300 bg-slate-50 p-3 font-mono text-xs leading-snug focus:border-violet-500 focus:bg-white focus:outline-none"
                rows={6}
                value={userDraft}
                onChange={(e) => setUserDraft(e.target.value)}
                spellCheck={false}
              />
            </div>
          ) : null}

          <div className="card p-4">
            <label className="text-sm font-medium text-slate-700">Notas (changelog)</label>
            <input
              type="text"
              className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
              placeholder="Ej: añade hint sobre IBAN…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-primary"
              onClick={() => void handleSave()}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Guardando…' : 'Guardar como nueva versión'}
            </button>
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => void handlePreview()}
              disabled={previewMutation.isPending}
            >
              {previewMutation.isPending ? 'Renderizando…' : 'Vista previa'}
            </button>
            <button
              type="button"
              className="ml-auto rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => void handleLoadDefault()}
              title="Carga la plantilla embebida por defecto en el editor. No guarda hasta que pulses 'Guardar como nueva versión'."
            >
              Restaurar plantilla por defecto
            </button>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-slate-900">Tokens disponibles</h3>
            <ul className="mt-3 space-y-2">
              {tokens.map((t) => (
                <li key={t.name} className="text-xs">
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-violet-700">{`{{${t.name}}}`}</code>
                  <p className="mt-0.5 text-slate-600">{t.description}</p>
                </li>
              ))}
              {tokens.length === 0 ? <li className="text-xs text-slate-500">Cargando…</li> : null}
            </ul>
          </div>

          <div className="card p-4">
            <h3 className="text-sm font-semibold text-slate-900">Versiones</h3>
            <ul className="mt-3 max-h-96 space-y-2 overflow-y-auto">
              {versions.map((v) => (
                <li
                  key={v.versionId}
                  className={`rounded-md border p-2 text-xs ${
                    v.isActive ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      className="font-mono text-slate-900 hover:text-violet-700"
                      onClick={() => setSelectedVersionId(v.versionId)}
                    >
                      v{v.number}
                    </button>
                    {v.isActive ? (
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                        Activa
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    {new Date(v.createdAt).toLocaleString()} — {v.createdBy}
                  </p>
                  {v.notes !== null ? <p className="mt-1 text-[11px] text-slate-700">{v.notes}</p> : null}
                  {!v.isActive ? (
                    <button
                      type="button"
                      className="mt-1 text-[11px] font-medium text-violet-700 hover:text-violet-900"
                      onClick={() => void handleActivate(v.versionId)}
                      disabled={activateMutation.isPending}
                    >
                      Activar esta versión
                    </button>
                  ) : null}
                </li>
              ))}
              {versions.length === 0 ? <li className="text-xs text-slate-500">Cargando…</li> : null}
            </ul>
          </div>
        </aside>
      </div>

      <PromptVersionComparison
        versions={versions.map<PromptVersionInfo>((v) => ({
          versionId: v.versionId,
          number: v.number,
          isActive: v.isActive,
        }))}
      />

      {previewOpen && previewResult !== null ? (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="card w-full max-w-3xl space-y-3 p-4">
            <header className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Vista previa renderizada</h2>
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-900"
                onClick={() => setPreviewOpen(false)}
              >
                Cerrar
              </button>
            </header>
            <p className="text-xs text-slate-500">
              Los tokens dinámicos se sustituyen por <code>[token]</code> para que veas la estructura sin
              necesitar un sample real.
            </p>
            <div>
              <h3 className="text-xs font-semibold uppercase text-slate-500">System</h3>
              <pre className="mt-1 max-h-72 overflow-auto rounded bg-slate-50 p-3 text-xs whitespace-pre-wrap">
                {previewResult.renderedSystem}
              </pre>
            </div>
            {previewResult.renderedUser !== null ? (
              <div>
                <h3 className="text-xs font-semibold uppercase text-slate-500">User</h3>
                <pre className="mt-1 max-h-40 overflow-auto rounded bg-slate-50 p-3 text-xs whitespace-pre-wrap">
                  {previewResult.renderedUser}
                </pre>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
